import {
  classifyFailure,
  formatFailure,
  parseOrchestratorDetail,
} from '@internal/plugin-stack-common';
import type { FailureClassification } from '@internal/plugin-stack-common';

/**
 * The Scaffolder client reports a rejected order as a plain `Error` whose
 * message is the status line and the response body run together:
 *
 *   `Backend request failed, 403 Forbidden {"error":{"message":"..."}}`
 *
 * (see `ScaffolderClient.scaffold` upstream). Nothing structured survives, so
 * the status and the body have to be read back out of the text before the
 * failure can be classified.
 */
const BACKEND_FAILURE = /Backend request failed,\s*(\d{3})\b([\s\S]*)$/;

function signalOfError(error: unknown): Parameters<typeof classifyFailure>[0] {
  // A `fetch` that never reached the backend carries the real reason on
  // `.cause`; the message is only ever "fetch failed".
  const cause = (error as { cause?: unknown } | undefined)?.cause;
  if (cause !== undefined) {
    return { cause };
  }

  // Anything shaped like a `ResponseError` answers directly, without parsing.
  const response = (error as { response?: { status?: unknown } } | undefined)
    ?.response;
  if (response && typeof response.status === 'number') {
    const body = (error as { body?: unknown }).body;
    return {
      status: response.status,
      detail: parseOrchestratorDetail(body),
    };
  }

  const message =
    typeof (error as { message?: unknown } | null)?.message === 'string'
      ? (error as { message: string }).message
      : String(error ?? '');
  const match = BACKEND_FAILURE.exec(message);
  if (match) {
    const status = Number(match[1]);
    const rest = match[2];
    // Only a body that is evidently JSON is read. The remainder is the HTTP
    // status text ("Forbidden"), which would otherwise be shown to the user
    // as though it were the platform's explanation.
    const bodyStart = rest.search(/[{[]/);
    return {
      status,
      detail:
        bodyStart >= 0
          ? parseOrchestratorDetail(rest.slice(bodyStart))
          : undefined,
    };
  }

  return { detail: message || undefined };
}

/**
 * Classifies whatever the Scaffolder threw when an order was rejected, so the
 * form reports a reason and a next step rather than the raw transport error.
 */
export function classifyScaffolderError(error: unknown): FailureClassification {
  return classifyFailure(signalOfError(error));
}

/**
 * Builds the `Error` handed to the ErrorApi. The title becomes the `name`,
 * which the error display shows as the heading, so it is left out of the
 * message to avoid printing it twice. A platform failure is labelled as such
 * so the reader can tell at a glance whether the next move is theirs.
 */
export function scaffolderErrorForDisplay(error: unknown): Error {
  const failure = classifyScaffolderError(error);
  const reported = new Error(formatFailure(failure, { includeTitle: false }));
  reported.name =
    failure.kind === 'user'
      ? failure.title
      : `Platform issue: ${failure.title}`;
  return reported;
}
